// Order state machine — TRS §6.6.
//
//   Draft ──► Submitted ──► InAssembly ──► Assembled ──► ReadyForShipment
//                                                    │          ▲
//                                                    └─► PackingOnPallet
//                                                               │
//                                                               ▼
//                                                          Delivered ──► Closed
//                 │   ▲                                         │
//                 ▼   │                                         ▼
//               OnHold (resumable to Submitted)              (terminal)
//                 │
//                 ▼
//              Cancelled (releases reservations) ── terminal
//
// Cancel is reachable from any pre-assembly state (Draft, Submitted, OnHold).
// Once a partner has started physical assembly (InAssembly), cancellation is
// no longer offered through this state machine — that's an admin override
// flow, not a normal lifecycle move.
export const ORDER_STATUSES = [
    'DRAFT',
    'SUBMITTED',
    'ON_HOLD',
    'IN_ASSEMBLY',
    'ASSEMBLED',
    'PACKING_ON_PALLET',
    'READY_FOR_SHIPMENT',
    'DELIVERED',
    'CLOSED',
    'CANCELLED',
];
export const TERMINAL_ORDER_STATUSES = new Set(['CLOSED', 'CANCELLED']);
const TRANSITIONS = {
    DRAFT: ['SUBMITTED', 'CANCELLED'],
    SUBMITTED: ['IN_ASSEMBLY', 'ON_HOLD', 'CANCELLED'],
    ON_HOLD: ['SUBMITTED', 'CANCELLED'],
    IN_ASSEMBLY: ['ASSEMBLED'],
    // Cancelling an already-assembled order is allowed (WMS-21): the components
    // are already written off and the built units stay in finished stock, so
    // cancel just releases the finished-unit reservations back to availability.
    ASSEMBLED: ['READY_FOR_SHIPMENT', 'CANCELLED'],
    // PACKING_ON_PALLET is entered only through the dedicated atomic packing
    // action, which also creates and fills the outbound pallets (WMS-104).
    PACKING_ON_PALLET: ['READY_FOR_SHIPMENT', 'CANCELLED'],
    READY_FOR_SHIPMENT: ['DELIVERED', 'CANCELLED'],
    DELIVERED: ['CLOSED'],
    CLOSED: [],
    CANCELLED: [],
};
export function canOrderTransition(from, to) {
    return TRANSITIONS[from].includes(to);
}
export function nextOrderStatuses(from) {
    return TRANSITIONS[from];
}
export function isOrderTerminal(s) {
    return TERMINAL_ORDER_STATUSES.has(s);
}
/**
 * Project reference/name may always be corrected on a draft by its placer.
 * After submission only warehouse staff may change them, and only until
 * physical assembly starts (WMS-69).
 */
export function canEditOrderProject(status, warehouseActor) {
    if (status === 'DRAFT')
        return true;
    return warehouseActor && (status === 'SUBMITTED' || status === 'ON_HOLD');
}
/**
 * A scoped partner may keep the customer-facing project name current until
 * physical assembly starts. Project reference editing retains the stricter
 * WMS-69 rule above (WMS-92).
 */
export function canEditOrderProjectName(status) {
    return status === 'DRAFT' || status === 'SUBMITTED' || status === 'ON_HOLD';
}
/**
 * Whole-pallet selection stays correctable until physical assembly completes.
 * The order row lock in the API serialises the last edit with the transition
 * to ASSEMBLED, where the shipment contents become fixed (WMS-102).
 */
export function canEditOrderPallets(status) {
    return (status === 'DRAFT' || status === 'SUBMITTED' || status === 'ON_HOLD' || status === 'IN_ASSEMBLY');
}
/**
 * Partner users may keep delivery details and documents current while an order
 * is being packed onto pallets. Once it is ready to leave the warehouse, only
 * warehouse staff retain those two correction paths (WMS-103, WMS-111).
 */
export function canEditOrderDelivery(status, warehouseActor) {
    if (status === 'DELIVERED' || status === 'CLOSED' || status === 'CANCELLED')
        return false;
    return status !== 'READY_FOR_SHIPMENT' || warehouseActor;
}
export function canEditOrderDocuments(status, warehouseActor) {
    if (warehouseActor)
        return true;
    return status !== 'READY_FOR_SHIPMENT' && status !== 'DELIVERED' && status !== 'CLOSED';
}
export function canEditOrderWorkCompletion(status) {
    return !['PACKING_ON_PALLET', 'READY_FOR_SHIPMENT', 'DELIVERED', 'CLOSED', 'CANCELLED'].includes(status);
}
export class OrderTransitionError extends Error {
    from;
    to;
    constructor(from, to) {
        super(`Order transition ${from} → ${to} is not allowed`);
        this.from = from;
        this.to = to;
        this.name = 'OrderTransitionError';
    }
}
export function assertOrderTransition(from, to) {
    if (!canOrderTransition(from, to))
        throw new OrderTransitionError(from, to);
}
export function reservationEffect(from, to) {
    if (to === 'SUBMITTED' && from === 'DRAFT')
        return 'CREATE';
    // Resuming from OnHold doesn't re-create reservations (they're still open).
    if (to === 'CANCELLED')
        return 'RELEASE';
    if (to === 'ASSEMBLED')
        return 'ASSEMBLE';
    if (to === 'DELIVERED')
        return 'SHIP';
    return 'NONE';
}
//# sourceMappingURL=order-state.js.map